import { useState, useEffect } from 'react';

import { AlertTriangle, Info, XCircle, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useAnnouncement } from '@/hooks/useAnnouncement';
import type { AnnouncementSeverity } from '@/lib/api/announcement';

const DISMISSED_KEY_PREFIX = 'announcement-dismissed:';

const SEVERITY_STYLES: Record<
  AnnouncementSeverity,
  { container: string; icon: string; message: string }
> = {
  info: {
    container:
      'bg-blue-50/80 dark:bg-blue-950/20 border-b border-blue-200/60 dark:border-blue-900/40 text-blue-950 dark:text-blue-50',
    icon: 'text-blue-600 dark:text-blue-400',
    message: 'text-blue-900/70 dark:text-blue-100/70',
  },
  warning: {
    container:
      'bg-amber-50/80 dark:bg-amber-950/20 border-b border-amber-200/60 dark:border-amber-900/40 text-amber-950 dark:text-amber-50',
    icon: 'text-amber-600 dark:text-amber-400',
    message: 'text-amber-900/70 dark:text-amber-100/70',
  },
  error: {
    container:
      'bg-rose-50/80 dark:bg-rose-950/20 border-b border-rose-200/60 dark:border-rose-900/40 text-rose-950 dark:text-rose-50',
    icon: 'text-rose-600 dark:text-rose-400',
    message: 'text-rose-900/70 dark:text-rose-100/70',
  },
};

const SEVERITY_ICONS: Record<AnnouncementSeverity, React.ComponentType<{ className?: string }>> = {
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
};

export function AnnouncementBanner(): React.ReactElement | null {
  const { config, loading, isActive } = useAnnouncement();
  const [dismissed, setDismissed] = useState(false);

  const announcementId = config.id || 'default';
  const storageKey = `${DISMISSED_KEY_PREFIX}${announcementId}`;
  const dismissible = config.dismissible !== false;

  useEffect(() => {
    if (!isActive || !dismissible) {
      setDismissed(false);
      return;
    }
    setDismissed(localStorage.getItem(storageKey) === '1');
  }, [isActive, dismissible, storageKey]);

  if (loading || !isActive || (dismissed && dismissible)) return null;
  if (!config.title && !config.message) return null;

  const severity: AnnouncementSeverity = config.severity ?? 'warning';
  const styles = SEVERITY_STYLES[severity];
  const Icon = SEVERITY_ICONS[severity];

  const handleDismiss = (): void => {
    localStorage.setItem(storageKey, '1');
    setDismissed(true);
  };

  return (
    <div
      className={cn(
        'flex items-start gap-3 px-4 py-3 text-sm',
        styles.container,
      )}
    >
      <Icon className={cn('h-4 w-4 flex-shrink-0 mt-0.5', styles.icon)} />
      <div className="flex-1 flex flex-col gap-0.5 leading-relaxed">
        {config.title && (
          <span className="font-semibold tracking-tight">{config.title}</span>
        )}
        {config.message && (
          <span className={styles.message}>{config.message}</span>
        )}
      </div>
      {dismissible && (
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 rounded-md text-current opacity-50 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/10 transition-all"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
